/**
 * DataHub API Client
 *
 * Provides location-based enrichment from ALL external toolkits:
 * - USGS (Soil, Groundwater, Hydrology)
 * - EPA (ECHO, CERCLIS, RCRAInfo, FRS, TRI)
 * - EPA Remediation (Superfund, FRTR, CLU-IN)
 * - Texas (RRC, TCEQ, TWDB)
 * - Oklahoma (OCC, OWRB, OKDEQ)
 * - New Mexico (NMWRRS, NMED)
 * - NGWA (National Ground Water Association)
 *
 * This is the FALLBACK for when EnviroData/GIS is not configured.
 * Always call this to fill in hydrogeology and regulatory data.
 */

import { apiClient } from './client';

// =============================================================================
// Types
// =============================================================================

export interface EnrichmentRequest {
  lat: number;
  lon: number;
  product?: 'POLYGON' | 'PATHFINDER' | 'PRECEPT' | string;
}

export interface SoilData {
  soil_type?: string;
  hydraulic_conductivity?: number;
  porosity?: number;
  hydrologic_group?: string;
  drainage_class?: string;
  clay_percentage?: number;
  sand_percentage?: number;
  organic_carbon_fraction?: number;
}

export interface GroundwaterData {
  nearby_wells?: number;
  nearest_well_distance_km?: number;
  aquifer_name?: string;
  depth_to_water_ft?: number;
  water_table_elevation_ft?: number;
  groundwater_flow_direction_deg?: number;
  hydraulic_gradient?: number;
}

export interface RegulatoryData {
  nearby_regulated_facilities?: number;
  facilities_with_violations?: number;
  nearest_violator_miles?: number;
  superfund_sites_nearby?: number;
  nearest_superfund_miles?: number;
  rcra_handlers_nearby?: number;
  tsdf_facilities_nearby?: number;
  tri_facilities_nearby?: number;
}

export interface WellInfo {
  well_id: string;
  latitude?: number;
  longitude?: number;
  depth_ft?: number;
  distance_from_source_ft?: number;
  source?: string;
}

export interface WellData {
  total_wells_found?: number;
  wells?: WellInfo[];
}

export interface PlumeData {
  known_plumes_nearby?: number;
  plume_area_sqft?: number;
  plume_length_ft?: number;
  plume_width_ft?: number;
  contaminant_type?: string;
  max_concentration_ug_l?: number;
}

export interface AutoFillData {
  // Hydrogeology - Critical for physics engine
  hydraulicConductivityFtDay?: number;
  aquiferKFtDay?: number;
  porosity?: number;
  aquiferPorosity?: number;
  hydraulicGradient?: number;
  groundwaterFlowDirectionDeg?: number;
  depthToWaterFt?: number;
  aquiferName?: string;
  soilType?: string;
  organicCarbonFraction?: number;
  aquiferThicknessFt?: number;
  transmissivityFt2Day?: number; // T = K × b
  elevationFt?: number;

  // Concentration
  maxConcentrationUgL?: number;
  maxConcentrationMgL?: number;
  contaminantConcentrationMgL?: number;
  contaminantType?: string;

  // Plume dimensions
  plumeAreaSqft?: number;
  plumeLengthFt?: number;
  plumeWidthFt?: number;

  // PSH
  pshPresent?: boolean;
  pshThicknessFt?: number;
  pshAreaSqft?: number;

  // Calculated values
  groundwaterVelocityFtDay?: number;
  retardationFactor?: number;

  // Wells
  monitoringWells?: WellInfo[];
}

export interface EnrichmentResponse {
  lat: number;
  lon: number;
  product?: string;
  timestamp: string;

  // Core data
  soil: SoilData;
  groundwater: GroundwaterData;
  regulatory: RegulatoryData;
  wells: WellData;
  plume: PlumeData;

  // Errors and metadata
  errors: string[];
  toolkits_called: string[];

  // Auto-fill for forms
  auto_fill: AutoFillData;
}

// =============================================================================
// API Functions
// =============================================================================

export const datahubApi = {
  /**
   * Enrich a location with data from ALL available toolkits.
   * This is the primary fallback when EnviroData/GIS is not configured.
   *
   * @param lat Latitude
   * @param lon Longitude
   * @param product Optional product name for targeted enrichment
   */
  async enrich(lat: number, lon: number, product?: string): Promise<EnrichmentResponse> {
    const response = await apiClient.post<EnrichmentResponse>('/datahub/enrich', {
      lat,
      lon,
      product,
    });
    return response.data;
  },

  /**
   * Quick enrichment (GET endpoint for testing)
   */
  async quickEnrich(lat: number, lon: number, product?: string): Promise<EnrichmentResponse> {
    const response = await apiClient.get<EnrichmentResponse>('/datahub/enrich/quick', {
      params: { lat, lon, product },
    });
    return response.data;
  },

  /**
   * List available toolkits
   */
  async listToolkits(): Promise<{ toolkits: Array<{ name: string; description: string }> }> {
    const response = await apiClient.get('/datahub/toolkits');
    return response.data;
  },
};

export default datahubApi;
